import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, getSessionId, getToken, API } from "@/lib/api";
import DocShareControl from "@/components/DocShareControl";
import { toast } from "sonner";
import {
    Upload, Loader2, FileText, Image as ImageIcon, Trash2, Download, Paperclip,
    ShieldCheck, ChevronDown,
} from "lucide-react";

/* CompletedDocsUpload
 * --------------------
 * Lets a caregiver attach signed copies of their legal docs (MPOA, DPOA,
 * Advance Directive, Living Will, POLST/MOLST) to their account.
 *
 * - Backend: /api/documents/uploads/{session_id} (POST list / GET list /
 *   DELETE / GET file).
 * - Files stored in Emergent object storage, indexed in MongoDB.
 * - File types: PDF, JPG/PNG/HEIC/WEBP, DOC/DOCX. Max 12 MB.
 */
const KIND_OPTIONS = [
    { id: "mpoa", label: "Medical Power of Attorney" },
    { id: "dpoa", label: "Durable Power of Attorney" },
    { id: "advance_directive", label: "Advance Directive" },
    { id: "living_will", label: "Living Will" },
    { id: "polst", label: "POLST / MOLST" },
    { id: "other", label: "Other document" },
];

function bytesPretty(n) {
    if (!Number.isFinite(n) || n <= 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function iconForContentType(ct) {
    if (!ct) return FileText;
    if (ct.startsWith("image/")) return ImageIcon;
    return FileText;
}

export default function CompletedDocsUpload() {
    const sid = getSessionId();
    const [open, setOpen] = useState(false);
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [kind, setKind] = useState("mpoa");
    const [label, setLabel] = useState("");
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const loadDocs = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/documents/uploads/${sid}`);
            setDocs(r.data || []);
        } catch (e) {
            // Soft-fail — caregiver may simply have nothing uploaded yet.
            if (e?.response?.status && e.response.status !== 404) {
                toast.error("Couldn't load your uploaded documents.");
            }
            setDocs([]);
        } finally {
            setLoading(false);
        }
    }, [sid]);

    useEffect(() => {
        loadDocs();
    }, [loadDocs]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 12 * 1024 * 1024) {
            toast.error("That file is too big. Please keep it under 12 MB.");
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        const form = new FormData();
        form.append("file", file);
        form.append("kind", kind);
        form.append("label", label.trim());

        setUploading(true);
        try {
            await api.post(`/documents/uploads/${sid}`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Saved.");
            setLabel("");
            await loadDocs();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(detail || "Upload failed. Try again?");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const remove = async (doc) => {
        if (!window.confirm(`Remove "${doc.label || doc.original_filename}" from your account? This can't be undone.`)) return;
        try {
            await api.delete(`/documents/uploads/${sid}/${doc.id}`);
            setDocs((prev) => prev.filter((d) => d.id !== doc.id));
            toast.success("Removed.");
        } catch (err) {
            toast.error("Couldn't remove that one.");
        }
    };

    // Downloads must include the auth header — we can't put it on an
    // <a href>, so fetch as blob and open via blob URL.
    const download = async (doc) => {
        try {
            const token = getToken();
            const resp = await fetch(`${API}/documents/uploads/${sid}/file/${doc.id}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            // Open in a new tab; the user can save from there.
            const win = window.open(url, "_blank", "noopener,noreferrer");
            if (!win) {
                // Pop-up blocked — fall back to a downloaded file.
                const a = document.createElement("a");
                a.href = url;
                a.download = doc.original_filename || `${doc.label || "document"}`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            }
            // Give the browser a beat to grab the blob before revoking.
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            toast.error("Couldn't open that file.");
        }
    };

    return (
        <section
            data-testid="completed-docs-uploads"
            className="ml-card p-0 bg-white border border-cream-dark overflow-hidden"
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid="completed-docs-toggle"
                className="w-full flex items-center justify-between gap-3 px-5 md:px-6 py-4 hover:bg-cream-light/60 transition-colors text-left"
            >
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-stage_early/60 flex items-center justify-center shrink-0">
                        <Paperclip size={18} strokeWidth={1.7} className="text-stage_early-dark" />
                    </div>
                    <div>
                        <h2 className="text-xl md:text-2xl font-heading font-medium text-[#12776F]">
                            Your completed documents
                        </h2>
                        <p className="text-sm text-[#4C5551] mt-0.5">
                            Have a signed MPOA, Living Will, POLST, or other planning document?
                            Save a copy here so it&apos;s easy to find when you need it.
                            {docs.length > 0 && (
                                <span className="ml-2 inline-flex items-center gap-1 text-stage_early-dark" data-testid="completed-docs-count">
                                    · {docs.length} saved
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-2xl bg-stage_early/40 border border-stage_early text-stage_early-dark shrink-0">
                    {open ? "Hide" : "Open"}
                    <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                </span>
            </button>

            {open && (
                <div className="px-5 md:px-6 pb-6">
                    {/* Upload form */}
                    <div className="rounded-2xl border border-cream-dark bg-cream-light p-4 md:p-5">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                            <label className="block md:col-span-1">
                                <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">
                                    Document type
                                </span>
                                <select
                                    value={kind}
                                    onChange={(e) => setKind(e.target.value)}
                                    data-testid="completed-docs-kind"
                                    className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                                >
                                    {KIND_OPTIONS.map((k) => (
                                        <option key={k.id} value={k.id}>{k.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="block md:col-span-2">
                                <span className="block text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-1">
                                    Label (optional)
                                </span>
                                <input
                                    type="text"
                                    value={label}
                                    onChange={(e) => setLabel(e.target.value)}
                                    placeholder={'e.g. "Mom\'s 2024 MPOA — Texas"'}
                                    data-testid="completed-docs-label"
                                    className="w-full px-3 py-2 rounded-2xl border border-cream-dark bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-soft"
                                />
                            </label>
                        </div>

                        <input
                            ref={fileRef}
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.heic,.webp,.doc,.docx,application/pdf,image/*"
                            onChange={handleUpload}
                            data-testid="completed-docs-file"
                            className="hidden"
                        />
                        <div className="flex flex-wrap gap-2 items-center">
                            <button
                                type="button"
                                disabled={uploading}
                                onClick={() => fileRef.current?.click()}
                                data-testid="completed-docs-upload"
                                className="px-5 py-2.5 rounded-2xl bg-teal-dark text-white text-sm font-medium hover:bg-teal disabled:opacity-60 inline-flex items-center gap-2"
                            >
                                {uploading
                                    ? <><Loader2 size={14} className="animate-spin" /> Uploading…</>
                                    : <><Upload size={14} /> Choose a file to upload</>
                                }
                            </button>
                            <p className="text-xs text-[#6E7A74]">
                                PDF, image, or Word — up to 12 MB.
                            </p>
                        </div>

                        <p className="mt-3 text-xs text-[#4C5551] flex items-start gap-1.5">
                            <ShieldCheck size={12} className="mt-0.5 shrink-0 text-stage_early-dark" />
                            Your files are private to your account. We never share them automatically — you control any access.
                        </p>
                    </div>

                    {/* Uploaded files list */}
                    <div className="mt-5" data-testid="completed-docs-list">
                        {loading ? (
                            <p className="text-sm text-[#6E7A74] inline-flex items-center gap-2">
                                <Loader2 size={14} className="animate-spin" /> Loading…
                            </p>
                        ) : docs.length === 0 ? (
                            <p className="text-sm text-[#6E7A74]">
                                Nothing saved here yet — once you upload, your documents will show up below.
                            </p>
                        ) : (
                            <ul className="space-y-2">
                                {docs.map((d) => {
                                    const Icon = iconForContentType(d.content_type);
                                    const kindLabel = KIND_OPTIONS.find((k) => k.id === d.kind)?.label || d.kind;
                                    return (
                                        <li
                                            key={d.id}
                                            data-testid={`completed-docs-row-${d.id}`}
                                            className="flex items-center gap-3 p-3 rounded-2xl bg-cream-light border border-cream-dark"
                                        >
                                            <div className="w-9 h-9 rounded-2xl bg-white border border-cream-dark flex items-center justify-center shrink-0">
                                                <Icon size={16} strokeWidth={1.7} className="text-stage_early-dark" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-[#1F2624] truncate">
                                                    {d.label || d.original_filename}
                                                </p>
                                                <p className="text-xs text-[#6E7A74] truncate">
                                                    {kindLabel} · {d.original_filename} · {bytesPretty(d.size)}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                <DocShareControl sid={sid} doc={d} onChanged={loadDocs} />
                                                <button
                                                    type="button"
                                                    onClick={() => download(d)}
                                                    data-testid={`completed-docs-download-${d.id}`}
                                                    title="Open / download"
                                                    className="p-2 rounded-full text-teal-dark hover:bg-teal-soft transition-colors"
                                                >
                                                    <Download size={14} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => remove(d)}
                                                    data-testid={`completed-docs-remove-${d.id}`}
                                                    title="Remove"
                                                    className="p-2 rounded-full text-coral-dark hover:bg-coral-light/60 transition-colors"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
